import { useState } from "react";
import { Plus, Trash2, ArrowDown, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_ROLES = [
  "AREA_MANAGER",
  "SALES_MANAGER",
  "REGIONAL_MANAGER",
  "FINANCE_MANAGER",
  "FACTORY_ADMIN",
  "SUPER_ADMIN",
];

/**
 * Visual editor for an approval chain (list of {step, role, label}).
 * Supports add, remove, reorder. Used by Order Workflow + Expense Workflow.
 */
export const ApprovalChainEditor = ({
  label = "Approval Flow",
  description = "Define the multi-step approval flow. Each step routes to a role for approval.",
  value = [],
  onChange,
  roles = DEFAULT_ROLES,
  testId = "approval-chain",
}) => {
  const [newRole, setNewRole] = useState(roles[0] || "");
  const [newLabel, setNewLabel] = useState("");
  const chain = Array.isArray(value) ? value : [];

  const addStep = () => {
    if (!newRole) return;
    const next = [
      ...chain,
      { step: chain.length + 1, role: newRole, label: newLabel || `${newRole.replace(/_/g, " ")} approval` },
    ];
    onChange(next.map((s, i) => ({ ...s, step: i + 1 })));
    setNewLabel("");
  };

  const removeStep = (idx) => {
    onChange(chain.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step: i + 1 })));
  };

  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= chain.length) return;
    const next = [...chain];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onChange(next.map((s, i) => ({ ...s, step: i + 1 })));
  };

  const updateLabel = (idx, lbl) => {
    const next = [...chain];
    next[idx] = { ...next[idx], label: lbl };
    onChange(next);
  };

  const updateRole = (idx, role) => {
    const next = [...chain];
    next[idx] = { ...next[idx], role };
    onChange(next);
  };

  return (
    <div className="py-2" data-testid={testId}>
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300">{label}</p>
      {description && <p className="text-[10px] text-slate-400 mb-2">{description}</p>}

      <div className="space-y-1.5">
        {chain.length === 0 && (
          <div className="text-[10px] text-slate-400 italic px-2 py-3 border border-dashed dark:border-slate-700 rounded text-center">
            No approval steps defined — orders will auto-approve
          </div>
        )}
        {chain.map((s, i) => (
          <div
            key={i}
            className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700"
            data-testid={`${testId}-step-${i}`}
          >
            <GripVertical className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <span className="text-[10px] font-bold text-slate-500 bg-white dark:bg-slate-800 rounded-full w-5 h-5 flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            <Select value={s.role} onValueChange={(v) => updateRole(i, v)}>
              <SelectTrigger className="h-7 text-xs w-44" data-testid={`${testId}-role-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={s.label || ""}
              onChange={(e) => updateLabel(i, e.target.value)}
              placeholder="Step label"
              className="flex-1 h-7 text-xs"
              data-testid={`${testId}-label-${i}`}
            />
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 leading-none"
                title="Move up"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === chain.length - 1}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 disabled:opacity-30 leading-none"
                title="Move down"
              >
                ▼
              </button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => removeStep(i)}
              className="h-6 w-6 p-0 text-red-500"
              data-testid={`${testId}-remove-${i}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>

      {/* Add new step */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t dark:border-slate-700">
        <ArrowDown className="w-3 h-3 text-slate-400" />
        <Select value={newRole} onValueChange={setNewRole}>
          <SelectTrigger className="h-7 text-xs w-44" data-testid={`${testId}-new-role`}>
            <SelectValue placeholder="Pick role" />
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addStep())}
          placeholder="Step label (optional)"
          className="flex-1 h-7 text-xs"
          data-testid={`${testId}-new-label`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addStep}
          className="h-7 px-2 border-[#8B1A1A] text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white"
          data-testid={`${testId}-add-step`}
        >
          <Plus className="w-3 h-3 mr-1" /> Add Step
        </Button>
      </div>
    </div>
  );
};

export default ApprovalChainEditor;
